import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, ButtonBase, Paper, Skeleton, Stack, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import LayersIcon from '@mui/icons-material/Layers';
import LinkIcon from '@mui/icons-material/Link';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import AdsApi from '../api/ads';
import SocialApi from '../api/social';
import { DEFAULT_SITE, listUrl } from '../components/ads/AdFilters';
import { NameCell } from '../components/ads/cells';
import { healthCounts } from '../components/connections/health';
import { resolveRange } from '../components/filters/dateRanges';
import { METRICS } from '../components/insights/metrics';
import StatTile from '../components/insights/StatTile';
import { formatDay } from '../utils/dates';
import { formatMoney, formatNumber } from '../utils/format';

const KPI_METRICS = METRICS.filter((metric) => ['spend', 'impressions', 'clicks', 'ctr'].includes(metric.id));
const QUERY = { site: DEFAULT_SITE };

/**
 * The landing page: a greeting, what needs attention, the steps left to set up, the year to date's totals, the
 * sections with their counts and the top ads. Each part loads on its own, so one failing request leaves the rest.
 */
export default function Home({ session }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let current = true;
    const one = { site: DEFAULT_SITE, page_size: 1 };
    Promise.allSettled([
      SocialApi.sites(),
      AdsApi.accounts({ site: DEFAULT_SITE, page_size: 100 }),
      AdsApi.campaigns(one),
      AdsApi.adsets(one),
      AdsApi.ads(one),
      AdsApi.insights({ site: DEFAULT_SITE, ...resolveRange({ range: 'ytd' }) }),
    ]).then(([sites, accounts, campaigns, adsets, ads, insights]) => {
      if (!current) return;
      const value = (result) => (result.status === 'fulfilled' ? result.value : null);
      setData({
        connections: value(sites)?.results.flatMap((site) => site.connections) ?? null,
        accounts: value(accounts)?.results ?? null,
        counts: { campaigns: value(campaigns)?.count, adsets: value(adsets)?.count, ads: value(ads)?.count },
        insights: value(insights),
      });
    });
    return () => {
      current = false;
    };
  }, []);

  const name = session?.user?.name?.split(' ')[0];
  return (
    <Box component="section">
      <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {name ? `Welcome back, ${name}` : 'Welcome'}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {session?.account?.name ? `Here is how ${session.account.name} is doing across your ad platforms.`
          : 'Here is how your ad platforms are doing.'}
      </Typography>

      {!data ? <LoadingFrame /> : (
        <Stack spacing={3}>
          <Attention connections={data.connections} />
          <GettingStarted data={data} />
          <Kpis insights={data.insights} />
          <Sections data={data} />
          <TopAds insights={data.insights} />
        </Stack>
      )}
    </Box>
  );
}

// Broken or expiring connections, with a way to fix them
function Attention({ connections }) {
  if (!connections) return null;
  const { error, warning } = healthCounts(connections);
  if (!error && !warning) return null;
  const text = [
    error && `${error} connection${error === 1 ? ' is' : 's are'} not working`,
    warning && `${warning} expire${warning === 1 ? 's' : ''} soon`,
  ].filter(Boolean).join(' and ');
  return (
    <Alert severity={error ? 'error' : 'warning'} sx={{ alignItems: 'center' }}
           action={<Button color="inherit" size="small" component={RouterLink} to="/connections">Fix now</Button>}>
      {text}. Their ad accounts stop syncing until they are reconnected.
    </Alert>
  );
}

// The setup steps, until all are done
function GettingStarted({ data }) {
  const steps = [
    { done: (data.connections?.length ?? 0) > 0, label: 'Connect a Facebook account', to: '/connections',
      action: 'Connect' },
    { done: (data.accounts ?? []).some((account) => account.is_enabled), label: 'Choose ad accounts to sync',
      to: '/ad-accounts', action: 'Choose' },
    { done: Boolean(data.insights && (data.insights.totals.impressions || Number(data.insights.totals.spend))),
      label: 'Get delivery data from the first sync', to: '/insights', action: 'View insights' },
  ];
  if (steps.every((step) => step.done)) return null;
  const doneCount = steps.filter((step) => step.done).length;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>Get set up</Typography>
        <Typography variant="body2" color="text.secondary">{doneCount} of {steps.length} done</Typography>
      </Stack>
      <Stack spacing={1}>
        {steps.map((step) => (
          <Stack key={step.label} direction="row" spacing={1.5} alignItems="center">
            {step.done ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color="disabled" />}
            <Typography sx={{ flex: 1, color: step.done ? 'text.secondary' : 'text.primary',
              textDecoration: step.done ? 'line-through' : 'none' }}>
              {step.label}
            </Typography>
            {!step.done && <Button size="small" component={RouterLink} to={step.to}>{step.action}</Button>}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

// The year to date's totals against the same number of days before it
function Kpis({ insights }) {
  if (!insights) return null;
  const currency = insights.currencies.length === 1 ? insights.currencies[0] : null;
  const previousLabel = `${formatDay(insights.previous.since)} – ${formatDay(insights.previous.until)}`;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
          Year to date
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {formatDay(insights.since)} – {formatDay(insights.until)}
          </Typography>
        </Typography>
        <Button size="small" component={RouterLink} to="/insights" endIcon={<ArrowForwardIcon />}>Insights</Button>
      </Stack>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
        {KPI_METRICS.map((metric) => (
          <StatTile key={metric.id} metric={metric} totals={insights.totals} previous={insights.previous.totals}
                    currency={currency} previousLabel={previousLabel} />
        ))}
      </Box>
    </Box>
  );
}

// Each part of the app with what it holds
function Sections({ data }) {
  const connections = data.connections ?? [];
  const accounts = data.accounts ?? [];
  const enabled = accounts.filter((account) => account.is_enabled).length;
  const synced = accounts.map((account) => account.last_synced_at).filter(Boolean).sort().pop();
  const sections = [
    { to: '/connections', label: 'Connections', icon: LinkIcon, value: formatNumber(connections.length),
      detail: 'Facebook accounts connected' },
    { to: '/ad-accounts', label: 'Ad accounts', icon: AccountBalanceWalletIcon, value: formatNumber(enabled),
      detail: `of ${formatNumber(accounts.length)} synced${synced ? `, last ${formatDay(synced.slice(0, 10))}` : ''}` },
    { to: '/campaigns', label: 'Campaigns', icon: CampaignIcon, value: formatNumber(data.counts.campaigns),
      detail: 'in your synced ad accounts' },
    { to: '/adsets', label: 'Ad sets', icon: LayersIcon, value: formatNumber(data.counts.adsets),
      detail: 'with targeting and budgets' },
    { to: '/ads', label: 'Ads', icon: ViewQuiltIcon, value: formatNumber(data.counts.ads),
      detail: 'with landing pages and utm_id' },
    { to: '/insights', label: 'Insights', icon: InsightsIcon, value: null, detail: 'Daily trends and top ads' },
  ];

  return (
    <Box>
      <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>Your ad data</Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {sections.map((section) => <SectionCard key={section.to} {...section} />)}
      </Box>
    </Box>
  );
}

function SectionCard({ to, label, icon: Icon, value, detail }) {
  const theme = useTheme();
  return (
    <ButtonBase component={RouterLink} to={to} sx={{
      display: 'flex', alignItems: 'center', gap: 2, p: 2.25, borderRadius: 3, textAlign: 'left',
      justifyContent: 'flex-start', border: 1, borderColor: 'divider', bgcolor: 'background.paper',
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.4),
        boxShadow: '0px 5px 10px 0px rgba(37, 45, 82, 0.08)' },
      '&:hover .arrow': { opacity: 1, transform: 'translateX(0)' },
    }}>
      <Avatar variant="rounded" sx={{ width: 44, height: 44, borderRadius: 2.5, color: 'primary.main',
        bgcolor: 'sidebar.active' }}>
        <Icon />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography sx={{ fontSize: value == null ? 15 : 22, fontWeight: 600, lineHeight: 1.3 }}>
          {value ?? detail}
        </Typography>
        {value != null && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">{detail}</Typography>
        )}
      </Box>
      <ArrowForwardIcon className="arrow" sx={{ color: 'primary.main', opacity: 0, transform: 'translateX(-4px)',
        transition: 'opacity 0.15s, transform 0.15s' }} />
    </ButtonBase>
  );
}

// The five ads with the most spend in the period
function TopAds({ insights }) {
  const navigate = useNavigate();
  const ads = insights?.top_ads.slice(0, 5) ?? [];
  if (ads.length === 0) return null;
  const top = Math.max(...ads.map((ad) => Number(ad.spend)), 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>Top ads by spend</Typography>
        <Button size="small" component={RouterLink} to="/insights" endIcon={<ArrowForwardIcon />}>All</Button>
      </Stack>
      {ads.map((ad) => (
        <ButtonBase key={`${ad.ad_account_id}-${ad.object_id}`} disabled={!ad.id}
                    onClick={() => navigate(listUrl('/ads', QUERY, { ad_account: ad.ad_account_id,
                      campaign: ad.campaign_id, adset: ad.adset_id, search: ad.object_id }))}
                    sx={{ display: 'flex', gap: 2, width: '100%', px: 2.5, py: 1.25, justifyContent: 'flex-start',
                      textAlign: 'left', borderTop: 1, borderColor: 'divider',
                      '&:hover': { bgcolor: 'table.rowHover' } }}>
          <NameCell name={ad.name} secondary={ad.campaign_name && `${ad.campaign_name} › ${ad.adset_name}`} />
          <Box sx={{ flex: 1, height: 8, borderRadius: 4,
            bgcolor: (theme) => alpha(theme.palette.primary.light, 0.12) }}>
            <Box sx={{ height: '100%', borderRadius: 4, bgcolor: 'primary.light',
              width: `${top ? (Number(ad.spend) / top) * 100 : 0}%` }} />
          </Box>
          <Typography variant="body2" sx={{ minWidth: 90, textAlign: 'right', fontWeight: 600 }}>
            {formatMoney(ad.spend, ad.currency)}
          </Typography>
        </ButtonBase>
      ))}
    </Paper>
  );
}

function LoadingFrame() {
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
        {KPI_METRICS.map((metric) => <Skeleton key={metric.id} variant="rounded" height={104} />)}
      </Box>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {[...Array(6)].map((_, index) => <Skeleton key={index} variant="rounded" height={86} />)}
      </Box>
    </Stack>
  );
}
